import { apiClient } from './client'
import type {
  FieldPermissionView,
  ObjectPermissionView,
  SecurityConfigCatalog,
  UiComponentAccessView,
} from '../types/securityConfig'

/**
 * IB-337 (≡ IB-88 ≡ IB-43) — `/api/v1/security-config/*` OLS/FLS/UI-component editors, plus the
 * Role/Territory tree node create/re-parent surface. Every call is capability-gated server-side
 * (`security_config:F` / `role:F` / `territory:F`) — a denied caller gets a real 403 `ApiError`.
 */

export async function getCatalog(signal?: AbortSignal): Promise<SecurityConfigCatalog> {
  const { data } = await apiClient.get<SecurityConfigCatalog>('/security-config/catalog', { signal })
  return data
}

export async function getObjectPermission(
  profileId: string,
  objectType: string,
  signal?: AbortSignal,
): Promise<ObjectPermissionView> {
  const { data } = await apiClient.get<ObjectPermissionView>(
    `/security-config/object-permissions?profileId=${profileId}&objectType=${encodeURIComponent(objectType)}`,
    { signal },
  )
  return data
}

export interface SaveObjectPermissionRequest {
  profileId: string
  objectType: string
  viewAll: boolean
  modifyAll: boolean
  reason: string
  rollback?: boolean
  sourceEventId?: string | null
}

export async function saveObjectPermission(request: SaveObjectPermissionRequest): Promise<ObjectPermissionView> {
  const { data } = await apiClient.put<ObjectPermissionView>('/security-config/object-permissions', request)
  return data
}

export async function listFieldPermissions(
  profileId: string,
  objectType: string,
  signal?: AbortSignal,
): Promise<FieldPermissionView[]> {
  const { data } = await apiClient.get<FieldPermissionView[]>(
    `/security-config/field-permissions?profileId=${profileId}&objectType=${encodeURIComponent(objectType)}`,
    { signal },
  )
  return data
}

export interface SaveFieldPermissionRequest {
  profileId: string
  objectType: string
  fieldName: string
  level: 'hidden' | 'read' | 'edit'
  reason: string
  rollback?: boolean
  sourceEventId?: string | null
}

export async function saveFieldPermission(request: SaveFieldPermissionRequest): Promise<FieldPermissionView> {
  const { data } = await apiClient.put<FieldPermissionView>('/security-config/field-permissions', request)
  return data
}

export async function listUiComponents(profileId: string, signal?: AbortSignal): Promise<UiComponentAccessView[]> {
  const { data } = await apiClient.get<UiComponentAccessView[]>(
    `/security-config/ui-components?profileId=${profileId}`,
    { signal },
  )
  return data
}

export interface SaveUiComponentAccessRequest {
  profileId: string
  componentKey: string
  visible: boolean
  reason: string
  rollback?: boolean
  sourceEventId?: string | null
}

export async function saveUiComponentAccess(request: SaveUiComponentAccessRequest): Promise<UiComponentAccessView> {
  const { data } = await apiClient.put<UiComponentAccessView>('/security-config/ui-components', request)
  return data
}

export async function createRole(key: string, name: string, parentId: string | null): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>('/roles', { key, name, parentId })
  return data
}

export async function reparentRole(roleId: string, newParentId: string | null): Promise<void> {
  await apiClient.post<void>(`/roles/${roleId}/reparent`, { newParentId })
}

export async function createTerritory(
  key: string,
  name: string,
  kind: 'geo' | 'named',
  parentId: string | null,
): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>('/territories', { key, name, kind, parentId })
  return data
}

export async function reparentTerritory(territoryId: string, newParentId: string | null): Promise<void> {
  await apiClient.post<void>(`/territories/${territoryId}/reparent`, { newParentId })
}
